import { expValEquals } from '@confluence/feature-experiments';

const LEGACY_TARGET_SELECTOR = "[data-group='deeplink'], h1, h2, h3, h4, h5, h6";

const toLegacyTarget = (candidate: Element): string | undefined =>
	(candidate.textContent || (candidate as HTMLAnchorElement).name)?.replace(/\s/g, '');

/**
 * Resolves a hash fragment written for the legacy (TinyMCE) renderer against the current DOM.
 *
 * Legacy pages identify headings and anchors as `PageTitle-TextWithoutSpaces`, and links authored
 * there keep that fragment after the page is migrated or converted. The current renderer gives
 * headings hyphenated ids and anchors a bare name, so such a fragment has no exact `id`/`name`
 * match. This finds the heading or anchor whose whitespace-stripped text (or name) the fragment
 * ends with, preferring the longest such match. A fragment that is exactly the stripped text,
 * with no title prefix, is a match too while the experiment is enabled.
 */
export const findLegacyAnchorTarget = (
	decodedHash: string,
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	container: ParentNode = document,
): HTMLElement | null => {
	if (!decodedHash) {
		return null;
	}

	let bestMatch: HTMLElement | null = null;
	let bestMatchIndex = Number.MAX_VALUE;

	for (const candidate of Array.from(container.querySelectorAll(LEGACY_TARGET_SELECTOR))) {
		if (!(candidate instanceof HTMLElement)) {
			continue;
		}

		const legacyTarget = toLegacyTarget(candidate);

		if (!legacyTarget || !decodedHash.endsWith(legacyTarget)) {
			continue;
		}

		const matchIndex = decodedHash.length - legacyTarget.length;
		if (
			matchIndex === 0 &&
			!expValEquals('confluence_legacy_anchor_hash_click_fallback', 'isEnabled', true)
		) {
			continue;
		}

		if (matchIndex < bestMatchIndex) {
			bestMatch = candidate;
			bestMatchIndex = matchIndex;
		}
	}

	return bestMatch;
};
